import React, { Fragment, useEffect, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Spinner, Table, Row, Col } from "reactstrap";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch } from "react-redux";
import { productDiscountSearch } from "../store/discountSlice";
import SelectProductTable from "./ProductSelectTable";

const schema = yup.object().shape({
  buy_products: yup
    .array()
    .of(yup.object().shape({ id: yup.number().required("Buy Product ID is required") }))
    .min(1, "At least one buy product must be selected."),
});

function DiscountProductSearchWindow({
  modalProps,
  onClose,
  initialSearchTerm,
  initialGetSearchTerm,
  onAddProduct,
  discountType,
  isGet,
  buyProducts,
  getProducts,
  initialSkuSearchTerm,
  initialGetSearchSkuTerm,
  onClearValue
}) {
  const dispatch = useDispatch();
  let variantRef = useRef([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [noDataMessage, setNoDataMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [localSearch, setlocalSearch] = useState(isGet ? initialGetSearchTerm : initialSearchTerm);
  const [localSkuSearch, setlocalSkuSearch] = useState(isGet ? initialGetSearchSkuTerm : initialSkuSearchTerm);
  const infiniteScrollRef = useRef();
  const [checkedIds, setCheckedIds] = useState([]);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      products: [],
      buy_products: buyProducts || [],
      get_products: getProducts || [],
    },
    resolver: yupResolver(schema),
  });

  const { fields: products } = useFieldArray({
    name: "products",
    control,
    keyName: "fieldId",
  });

  const { append: appendBuyProduct, remove: removeBuyProduct } = useFieldArray({
    name: "buy_products",
    control,
  });

  const { append: appendGetProduct, remove: removeGetProduct } = useFieldArray({
    name: "get_products",
    control,
  });

  const productSearchFunction = (isConcat, page) => {
    const parentValue = initialSearchTerm || initialGetSearchTerm;
    const parentSkuValue = initialSkuSearchTerm || initialGetSearchSkuTerm;

    if (!localSearch && parentValue) {
      setlocalSearch(parentValue);
    }

    if (!localSkuSearch && parentSkuValue) {
      setlocalSkuSearch(parentSkuValue);
    }

    if ((localSearch || localSkuSearch) || (!parentValue || !parentSkuValue)) {
      setNoDataMessage(null);
      dispatch(productDiscountSearch({ name: localSearch, sku: localSkuSearch, page: page })).then((res) => {
        onClearValue();
        if (!res.error) {
          // dispatch(setProducts(res.payload))
          if (res.payload.data.length === 0) {
            setNoDataMessage(
              "No products found with that name, or the product is already part of another active discount. Please try again with a different name!"
            );
          }
          if (isConcat) {
            reset({
              products: [...getValues("products"), ...res.payload.data],
              buy_products: buyProducts || [],
              get_products: discountType === 2 ? getValues("get_products") : [],
            });
          } else {
            reset({
              products: res.payload.data,
              buy_products: buyProducts || [],
              get_products: discountType === 2 ? getValues("get_products") : [],
            });
          }
        }
        setHasMore(res.payload.pagination.has_more);
      });
    }
  };

  useEffect(() => {
    if (modalProps.props.isOpen) {
      if (infiniteScrollRef.current) {
        infiniteScrollRef.current.actionTriggered = false;
      }
      const timer = setTimeout(() => {
        productSearchFunction(false, 1);
      }, 1000);

      // cleanup
      return () => clearTimeout(timer);
    }
  }, [modalProps.props, localSearch, localSkuSearch]);

  useEffect(() => {
    if (!modalProps.props.isOpen) {
      setlocalSearch("");
      setlocalSkuSearch("");
    }
  }, [modalProps.props]);

  const fetchMoreProducts = async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const res = await dispatch(productDiscountSearch({ name: localSearch, sku: localSkuSearch, page: currentPage + 1 }));
      const fetchedProducts = res.payload.data.map((product) => ({
        ...product,
        selected: false,
      }));
       console.log("Has More", res)
      reset({
        products: [...getValues("products"), ...fetchedProducts],
        buy_products: getValues("buy_products"),
        get_products: discountType === 2 ? getValues("get_products") : [],
      });

      setCurrentPage((prevPage) => prevPage + 1);
      setHasMore(res.payload.pagination.has_more);
    } catch (error) {
      console.error("Error fetching more products:", error);
    } finally {
      setLoading(false);
    }
  };


  const isCheckedItem = (item) => {
    let orderProducts = isGet ? getValues("get_products") : getValues("buy_products");
    let orderedProductIndex = orderProducts.findIndex((x) => x.variant_id === item.id);
    return orderedProductIndex !== -1;
  };

  const onSubmit = (data) => {
    const selectedBuyProducts = getValues("buy_products");
    const selectedGetProducts = discountType === 2 ? getValues("get_products") : [];
    onAddProduct({ buy: selectedBuyProducts, get: selectedGetProducts });
    onClose();
  };

  const variantOnChange = (e, product, variant, isGet) => {
    if (variant.price <= 0) {
      return;
    }
    let orderProducts = isGet ? getValues("get_products") : getValues("buy_products");
    orderProducts = productObjectUpdate(orderProducts, e.target.checked, product, variant);

    if (isGet) {
      setValue("get_products", orderProducts);
    } else {
      setValue("buy_products", orderProducts);
    }
  };

  const findProductIndex = (productId) => {
    const productData = isGet ? getValues("get_products") : getValues("buy_products");
    return productData.findIndex((item) => item.id === productId);
  };

  const productOnChange = (e, product, variants, isGet) => {
    let orderProducts = isGet ? getValues("get_products") : getValues("buy_products");
    variants.map((item, i) => {
      if (!item.is_out_of_stock && item.price > 0) {
        variantRef.current[item.id].checked = e.target.checked;
        orderProducts = productObjectUpdate(orderProducts, e.target.checked, product, item);
      }
    });

    if (isGet) {
      setValue("get_products", orderProducts);
    } else {
      setValue("buy_products", orderProducts);
    }
  };

  const isCheckedProduct = (variants) => {
    let orderProducts = (isGet ? getValues("get_products") : getValues("buy_products")) || [];
    let avaliableItemsCount = 0;
    let checkedItemsCount = 0;
    variants.map((item, i) => {
      if (!item.is_out_of_stock && item.price > 0) {
        avaliableItemsCount++;
        let orderedProductIndex = orderProducts.findIndex((x) => x.variant_id === item.id);
        if (orderedProductIndex !== -1) {
          checkedItemsCount++;
        }
      }
    });

    return avaliableItemsCount !== 0 && avaliableItemsCount === checkedItemsCount;
  };

  const productObjectUpdate = (orderProducts, isCheck, product, variant) => {
    let orderedProductIndex = orderProducts.findIndex((x) => x.variant_id === variant.id);
    if (orderedProductIndex != "-1") {
      if (!isCheck) {
        orderProducts.splice(orderedProductIndex, 1);
      }
    } else {
      orderProducts.push({
        product_id: product.id,
        variant_id: variant.id,
        product_variant_id: variant.product_variant_id,
        product_name: product.name,
        image: product.url,
        price: variant.price,
        sku: variant.sku,
        variant_name: variant.variant_name,
        quantity: 1,
        total_price: variant.price,
        stock: variant.stock,
        title: variant.title,
        size: variant.size,
        colour: variant.colour,
      });
    }

    setCheckedIds((prevCheckedIds) => {
      if (prevCheckedIds.includes(variant.id)) {
        return prevCheckedIds.filter((checkedId) => checkedId !== variant.id);
      } else {
        return [...prevCheckedIds, variant.id];
      }
    });

    return orderProducts;
  };

  return (
    <Modal {...modalProps.props} toggle={onClose} size="lg" centered className="product-modal">
      <ModalHeader toggle={onClose}>Product List</ModalHeader>
      <ModalBody style={{ minHeight: 500 }}>
        <Row>
          <Col sm={6}>
            <Input type="text" placeholder="Search Products" value={localSearch} onChange={(e) => setlocalSearch(e.target.value)} className="mb-3" />
          </Col>
          <Col sm={6}>
            <Input type="text" placeholder="Search by SKU" value={localSkuSearch} onChange={(e) => setlocalSkuSearch(e.target.value)} className="mb-3" />
          </Col>
        </Row>
        {products.length !== 0 ? (
          <InfiniteScroll
            ref={(scroll) => {
              infiniteScrollRef.current = scroll;
            }}
            dataLength={products.length}
            hasMore={hasMore}
            pullDownToRefresh={true}
            refreshFunction={() => {
              console.log("Loading");
            }}
            next={fetchMoreProducts}
            loader={
              <div className="text-center">
                <Spinner size="sm" />
              </div>
            }
            height={400}
            scrollThreshold={0.9}
          >
            {products.map((item, index) => (
              <Table bordered size="sm" className="mt-2">
                <tbody>
                  <Fragment key={item.id}>
                    <tr>
                      <td style={{ width: "5%", height: 1 }}>
                        <input
                          type={"checkbox"}
                          checked={isCheckedProduct(item.variants)}
                          onChange={(e) => {
                            productOnChange(e, item, item.variants, isGet);
                          }}
                        />
                      </td>
                      <td style={{ width: "15%", textAlign: "center" }}>
                        <img width={20} src={item.url} />
                      </td>
                      <td style={{ textAlign: "left" }}>{item.name}</td>
                    </tr>
                    <tr>
                      <td
                        colSpan={3}
                        style={{
                          paddingTop: 0,
                          paddingRight: 25,
                          paddingLeft: 25,
                          border: "none",
                        }}
                      >
                        <table className="table" style={{ width: "100%", marginTop: 0 }}>
                          <tbody>
                            {item.variants.map((itemVariant, v) => (
                              <tr key={v}>
                                <td style={{ width: "5%" }}>
                                  <input
                                    ref={(ref) => {
                                      variantRef.current[itemVariant.id] = ref;
                                    }}
                                    disabled={itemVariant.is_out_of_stock || itemVariant.price <= 0}
                                    checked={isCheckedItem(itemVariant)}
                                    type={"checkbox"}
                                    onChange={(e) => {
                                      variantOnChange(e, item, itemVariant, isGet);
                                    }}
                                  />
                                </td>

                                <td
                                  style={{
                                    width: "50%",
                                    textAlign: "left",
                                  }}
                                >
                                  {itemVariant.variant_name}
                                </td>

                                <td style={{ textAlign: "right" }}>{itemVariant.format_stock}</td>
                                <td style={{ textAlign: "right" }}>£ {itemVariant.price}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </Fragment>
                </tbody>
              </Table>
            ))}
          </InfiniteScroll>
        ) : noDataMessage ? (
          noDataMessage
        ) : (
          <div className="text-center">
            <Spinner size="sm" />
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={() =>onSubmit()} disabled={false}>
          Add Productsss
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default DiscountProductSearchWindow;
